import { cx } from "@emotion/css";
import {
  faCalendar,
  faCar,
  faComment,
  faCow,
  faCrown,
  faExpand,
  faInfo,
  faMap,
  faMountain,
  faPhotoFilm,
  faSeedling,
  faStar,
  faTree,
  faUser,
  faWater,
  type IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "maplibre-gl/dist/maplibre-gl.css";
import type { TFunction } from "i18next";
import { memo, useEffect, useRef, useState } from "react";
import { Badge, Button, Card, OverlayTrigger, Spinner, Stack, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { swisstopoUrl } from "../services/coords";
import type {
  CampsiteDataFragment,
  CampsiteTraitDataFragment,
  CampsiteTraitDistanceDataFragment,
  CampsiteTraitsFragment,
  GetCampsiteQuery,
} from "../types/graphql/graphql";
import styles from "./CampsiteCard.module.css";
import { CampsiteDetail } from "./CampsiteDetail";
import CampsiteMap from "./CampsiteMap";

export type CampsiteCardTab = "info" | "availability" | "reports" | "photos";

export function getEnvAttributes(
  traits: CampsiteTraitsFragment,
  t: TFunction<"translation", undefined>,
): {
  [key in keyof CampsiteTraitsFragment]?: CampsiteAttribute;
} {
  const { envForest, envLake, envMeadow, envRiver, envStream, envWillow, carAccess } = traits;

  return {
    envForest: {
      trait: envForest,
      icon: faTree,
      label: t("activerecord.attributes.campsite.env_forest"),
    },
    envLake: {
      trait: envLake,
      icon: faWater,
      label: t("activerecord.attributes.campsite.env_lake"),
    },
    envMeadow: {
      trait: envMeadow,
      icon: faSeedling,
      label: t("activerecord.attributes.campsite.env_meadow"),
    },
    envRiver: {
      trait: envRiver,
      icon: faWater,
      label: t("activerecord.attributes.campsite.env_river"),
    },
    envStream: {
      trait: envStream,
      icon: faWater,
      label: t("activerecord.attributes.campsite.env_stream"),
    },
    envWillow: {
      trait: envWillow,
      icon: faCow,
      label: t("activerecord.attributes.campsite.env_willow"),
    },
    carAccess: {
      trait: carAccess,
      icon: faCar,
      label: t("activerecord.attributes.campsite.car_access"),
    },
  };
}

const tabTranslationKeys = {
  info: "public.campsites.campsite.sections.info",
  availability: "public.campsites.campsite.sections.availability",
  reports: "public.campsites.campsite.sections.reports",
  photos: "public.campsites.campsite.sections.photos",
} as const;

type CampsiteAttribute = {
  trait: CampsiteTraitDataFragment | CampsiteTraitDistanceDataFragment;
  label: string;
  icon: IconDefinition;
};

export type CampsiteCardProps = {
  campsite: CampsiteDataFragment;
  details?: GetCampsiteQuery["campsite"];
  detailsLoading?: boolean;
  onExpand?: (ref: string) => void;
  onToggleFavorite?: (campsiteId: number) => void;
  isFavorite?: boolean;
  withMap?: boolean;
};

export const CampsiteCard = memo(function CampsiteCard({
  campsite: {
    id,
    ref,
    name,
    zip,
    place,
    canton,
    maxHeadcount,
    altitude,
    area,
    traits,
    photosCount,
    publicReportsCount,
    premium,
    selfUrl,
    wgs84Coordinates,
    mapPreviewUrl,
    lv03Coordinates,
  },
  details,
  detailsLoading,
  onExpand,
  isFavorite,
  onToggleFavorite,
}: CampsiteCardProps) {
  const expanded = !!details || detailsLoading;
  const { t } = useTranslation();
  const envAttributes = getEnvAttributes(traits, t);
  const appliedEnvironments = Object.values(envAttributes)
    .filter((attr): attr is CampsiteAttribute => !!attr)
    .filter(({ trait }) => trait.applies);
  const [selectedTab, setSelectedTab] = useState<CampsiteCardTab>("info");
  const handleSelectTab = (tab?: CampsiteCardTab) => {
    setSelectedTab(tab ?? "info");
  };
  const elementRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: also needs to run when tabs change
  useEffect(() => {
    if (expanded) elementRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [expanded, selectedTab]);

  return (
    <Card
      key={id}
      id={ref}
      ref={elementRef}
      className={cx(styles.card, "bg-light", { [styles.expanded]: expanded, "shadow-sm": expanded })}
    >
      <div>
        {premium && (
          <div className={styles.premium}>
            <FontAwesomeIcon icon={faCrown} size="lg" color="white" />
          </div>
        )}
        {wgs84Coordinates &&
          (expanded ? (
            <CampsiteMap wgs84Coordinates={wgs84Coordinates} swisstopoUrl={swisstopoUrl(lv03Coordinates)} />
          ) : (
            <div className={styles.mapPreview}>
              {mapPreviewUrl && <img loading="lazy" src={mapPreviewUrl} alt="map preview" />}
              <button type="button" className="shadow-none border-0" onClick={() => !expanded && onExpand?.(ref)}>
                <FontAwesomeIcon icon={faExpand} />
                {t("public.campsites.campsite.expand")}
              </button>
            </div>
          ))}
      </div>

      <Card.Header className="pb-3 bg-light">
        <div className="pb-3">
          <Card.Title className="d-flex justify-content-between m-0 pt-1 align-items-center gap-3">
            <div className="d-flex flex-grow-1 flex-wrap align-items-center">
              <button
                onClick={() => {
                  !expanded && onExpand?.(ref);
                }}
                type="button"
                className="p-0 btn btn-link text-decoration-none text-start lh-sm me-3 text-break"
              >
                <span className={cx("font-display", { "fs-4": !expanded }, { "fs-1": expanded })}>{name}</span>
              </button>
              <div>{t("public.campsites.campsite.in", { zip, place, canton: canton?.toUpperCase() })}</div>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2 justify-content-end">
              <a className="badge bg-secondary ms-3 text-sm text-decoration-none text-primary" href={selfUrl}>
                {ref}
              </a>
              {onToggleFavorite && (
                <Button className={cx(styles.favToggle, "p-0")} variant="link" onClick={() => onToggleFavorite(id)}>
                  <FontAwesomeIcon color={isFavorite ? "var(--bs-yellow)" : undefined} icon={faStar} size="lg" />
                </Button>
              )}
            </div>
          </Card.Title>
        </div>
        <Stack gap={2}>
          <Stack direction="horizontal" gap={2} style={{ flexWrap: "wrap" }}>
            {!!maxHeadcount && (
              <OverlayTrigger
                trigger={["hover", "focus"]}
                overlay={<Tooltip>{t("activemodel.attributes.campsite/filter.headcount")}</Tooltip>}
              >
                <Badge pill className="bg-secondary text-primary">
                  <FontAwesomeIcon icon={faUser} className="me-1" />
                  {maxHeadcount}
                </Badge>
              </OverlayTrigger>
            )}
            {!!altitude && (
              <Badge pill className="bg-secondary text-primary">
                <FontAwesomeIcon icon={faMountain} className="me-1" />
                {t("format.altitude", { number: altitude })}
              </Badge>
            )}
            {!!area && (
              <Badge className="bg-secondary text-primary" pill>
                <FontAwesomeIcon icon={faMap} className="me-1" />
                <span>
                  {area} m<sup>2</sup>
                </span>
              </Badge>
            )}
            {appliedEnvironments.map(({ icon, label, trait }) => (
              <OverlayTrigger
                show={trait.remarks ? undefined : false}
                trigger={["hover", "focus"]}
                key={label}
                overlay={<Tooltip>{trait.remarks}</Tooltip>}
              >
                <Badge pill className="bg-secondary text-primary">
                  <FontAwesomeIcon icon={icon} className="me-1" />
                  {label} {"value" in trait && trait.value ? `(${trait.value} m)` : ""}
                </Badge>
              </OverlayTrigger>
            ))}
          </Stack>
        </Stack>
      </Card.Header>

      <div className={cx({ "d-none": !expanded })}>
        {expanded && detailsLoading ? (
          <Spinner className="d-block mx-auto my-5 text-center" />
        ) : (
          details && (
            <CampsiteDetail
              id={id}
              campsiteDetail={details}
              selectedTab={selectedTab}
              onSelectedTabChange={setSelectedTab}
            />
          )
        )}
      </div>

      {expanded && (
        <Card.Footer className={cx("bg-light", styles.actions)}>
          <Button
            key="info"
            variant="link"
            data-active={expanded && selectedTab === "info"}
            onClick={() => handleSelectTab("info")}
          >
            <div className={styles.tabIconContainer} title={t(tabTranslationKeys.info)}>
              <FontAwesomeIcon icon={faInfo} size="lg" />
            </div>
            <span className="d-none d-md-block">{t(tabTranslationKeys.info)}</span>
          </Button>
          <Button
            key="availability"
            variant="link"
            data-active={expanded && selectedTab === "availability"}
            onClick={() => handleSelectTab("availability")}
          >
            <div className={styles.tabIconContainer} title={t(tabTranslationKeys.availability)}>
              <FontAwesomeIcon icon={faCalendar} size="lg" />
            </div>
            <span className="d-none d-md-block">{t(tabTranslationKeys.availability)}</span>
          </Button>
          <Button
            key="reports"
            variant="link"
            data-active={expanded && selectedTab === "reports"}
            onClick={() => handleSelectTab("reports")}
          >
            <div className={styles.tabIconContainer} title={t(tabTranslationKeys.reports)}>
              <FontAwesomeIcon icon={faComment} size="lg" />
              {!!publicReportsCount && <Badge className="bg-secondary text-primary">{publicReportsCount}</Badge>}
            </div>
            <span className="d-none d-md-block">{t(tabTranslationKeys.reports)}</span>
          </Button>
          {!!photosCount && (
            <Button
              key="photos"
              variant="link"
              data-active={expanded && selectedTab === "photos"}
              onClick={() => handleSelectTab("photos")}
            >
              <div className={styles.tabIconContainer} title={t(tabTranslationKeys.photos)}>
                <FontAwesomeIcon icon={faPhotoFilm} size="lg" />
                {<Badge className="bg-secondary text-primary">{photosCount}</Badge>}
              </div>
              <span className="d-none d-md-block">{t(tabTranslationKeys.photos)}</span>
            </Button>
          )}
        </Card.Footer>
      )}
    </Card>
  );
});
